import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Lightning,
  Envelope,
  Target,
  FileText,
  ClipboardText,
  CreditCard,
  ShieldWarning,
  CircleNotch,
  FunnelSimple,
  ArrowsClockwise,
  Phone,
  ChatCircle,
  Globe,
  UserPlus,
  CalendarBlank,
  TrendUp,
  Users,
  CaretDown
} from '@phosphor-icons/react';

const TYPE_CONFIG = {
  email: { icon: Envelope, color: 'text-sky-600 bg-sky-50 border-sky-200', label: 'Email' },
  lead: { icon: Target, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Lead' },
  quote: { icon: FileText, color: 'text-violet-600 bg-violet-50 border-violet-200', label: 'Cotizacion' },
  work_order: { icon: ClipboardText, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Orden' },
  payment: { icon: CreditCard, color: 'text-green-600 bg-green-50 border-green-200', label: 'Pago' },
  audit: { icon: ShieldWarning, color: 'text-rose-600 bg-rose-50 border-rose-200', label: 'Auditoria' },
};

const SUBTYPE_ICONS = {
  retell: Phone,
  chatbot: ChatCircle,
  website_quote: Globe,
  manual: UserPlus,
  welcome: Envelope,
  follow_up: ArrowsClockwise,
  notification: Envelope,
  gc_report: FileText,
};

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={20} className={accent || 'text-slate-400'} />
      </div>
      <p className="text-2xl font-bold text-slate-900 tracking-tight">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function TimelineDot({ type }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.lead;
  const Icon = config.icon;
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${config.color} shrink-0`}>
      <Icon size={16} weight="bold" />
    </div>
  );
}

function ActivityItem({ item, selected, onToggle }) {
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.lead;
  const SubIcon = SUBTYPE_ICONS[item.subtype];
  const ts = item.timestamp ? new Date(item.timestamp) : null;
  const timeStr = ts ? ts.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  const isLead = item.type === 'lead';

  return (
    <div className="flex gap-3 py-3 group" data-testid={`activity-item-${item.id}`}>
      {isLead && (
        <input type="checkbox" checked={selected} onChange={() => onToggle(item.related_id || item.id)}
          className="w-4 h-4 mt-2 accent-blue-600 shrink-0" onClick={e => e.stopPropagation()} />
      )}
      {!isLead && <div className="w-4 shrink-0" />}
      <TimelineDot type={item.type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{item.title}</p>
            <p className="text-xs text-slate-500 truncate">{item.description}</p>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span className="text-[10px] text-slate-400">{timeStr}</span>
            <div className="flex items-center gap-1 mt-0.5">
              {SubIcon && <SubIcon size={10} className="text-slate-400" />}
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color}`}>
                {config.label}
              </span>
            </div>
          </div>
        </div>
        {item.actor && item.actor !== 'Sistema' && item.actor !== 'Auto' && (
          <p className="text-[10px] text-slate-400 mt-0.5">Por: {item.actor}</p>
        )}
      </div>
    </div>
  );
}

export default function ActivityPage() {
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(7);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [resetting, setResetting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === activities.filter(a => a.type === 'lead').length) setSelectedIds(new Set());
    else setSelectedIds(new Set(activities.filter(a => a.type === 'lead').map(a => a.related_id || a.id)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedIds.size} leads seleccionados?`)) return;
    try {
      await axios.post(`${API_URL}/api/bulk/delete-leads`, { ids: [...selectedIds] }, { withCredentials: true });
      setSelectedIds(new Set());
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleResetAllData = async () => {
    if (!window.confirm('ATENCION: Esto eliminara TODOS los datos del CRM (leads, clientes, cotizaciones, facturas, prospectos, emails, OTs, planos, etc). Solo se conservan usuarios y catalogo de precios. Continuar?')) return;
    if (!window.confirm('SEGUNDA CONFIRMACION: Esta seguro? Esta accion NO se puede deshacer.')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/reset-all-data`, {}, { withCredentials: true });
      alert(`Reset completado: ${res.data.total_deleted} documentos eliminados. Recargando...`);
      window.location.reload();
    } catch (e) {
      alert('Error al resetear: ' + (e.response?.data?.detail || e.message));
    }
    setResetting(false);
  };

  const fetchData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.append('days', daysFilter);
      if (typeFilter) params.append('activity_type', typeFilter);

      const [feedRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/activity/feed?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/activity/stats`, { withCredentials: true })
      ]);
      setActivities(feedRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error loading activity:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [typeFilter, daysFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const typeCounts = {};
  activities.forEach(a => { typeCounts[a.type] = (typeCounts[a.type] || 0) + 1; });

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="activity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Actividad</h1>
          <p className="text-slate-500">Timeline de toda la actividad del CRM en tiempo real</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleResetAllData} disabled={resetting}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 text-sm"
            data-testid="reset-all-data">
            {resetting ? 'Reseteando...' : 'Resetear Todo'}
          </button>
          <button
            onClick={() => fetchData(false)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium disabled:opacity-50"
            data-testid="refresh-activity"
          >
            <ArrowsClockwise size={18} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="activity-stats">
          <StatCard label="Leads Hoy" value={stats.leads_today} icon={Target} accent="text-emerald-500" />
          <StatCard label="Leads Semana" value={stats.leads_week} icon={TrendUp} accent="text-blue-500" />
          <StatCard label="Emails Hoy" value={stats.emails_today} icon={Envelope} accent="text-sky-500" />
          <StatCard label="Emails Semana" value={stats.emails_week} icon={Envelope} accent="text-cyan-500" />
          <StatCard label="Cotizaciones Sem." value={stats.quotes_week} icon={FileText} accent="text-violet-500" />
          <StatCard label="OT Activas" value={stats.work_orders_active} icon={ClipboardText} accent="text-amber-500" />
        </div>
      )}

      {/* Totals row */}
      {stats && (
        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 bg-white rounded-lg border border-slate-200 text-sm">
            <span className="text-slate-500">Total Leads:</span>{' '}
            <span className="font-bold text-slate-900">{stats.total_leads}</span>
          </div>
          <div className="px-4 py-2 bg-white rounded-lg border border-slate-200 text-sm">
            <span className="text-slate-500">Total Clientes:</span>{' '}
            <span className="font-bold text-slate-900">{stats.total_clients}</span>
          </div>
          <div className="px-4 py-2 bg-white rounded-lg border border-slate-200 text-sm">
            <span className="text-slate-500">Total Cotizaciones:</span>{' '}
            <span className="font-bold text-slate-900">{stats.total_quotes}</span>
          </div>
          <div className="px-4 py-2 bg-white rounded-lg border border-slate-200 text-sm">
            <span className="text-slate-500">Leads Mes:</span>{' '}
            <span className="font-bold text-slate-900">{stats.leads_month}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <FunnelSimple size={20} className="text-slate-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            data-testid="activity-type-filter"
          >
            <option value="">Toda la actividad</option>
            <option value="lead">Leads</option>
            <option value="email">Emails</option>
            <option value="quote">Cotizaciones</option>
            <option value="work_order">Ordenes de Trabajo</option>
            <option value="payment">Pagos</option>
            <option value="audit">Auditoria</option>
          </select>
          <select
            value={daysFilter}
            onChange={(e) => setDaysFilter(Number(e.target.value))}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            data-testid="activity-days-filter"
          >
            <option value={1}>Hoy</option>
            <option value={7}>Ultima semana</option>
            <option value={30}>Ultimo mes</option>
            <option value={90}>Ultimos 3 meses</option>
            <option value={365}>Todo el ano</option>
          </select>

          {/* Type badges */}
          <div className="flex flex-wrap gap-1.5 ml-auto">
            {Object.entries(typeCounts).map(([type, count]) => {
              const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.lead;
              const Icon = cfg.icon;
              return (
                <span key={type} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
                  <Icon size={12} />
                  {cfg.label}: {count}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bulk Delete Bar */}
      {selectedIds.size > 0 && (
        <div data-testid="bulk-delete-bar" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-red-700 font-medium text-sm">{selectedIds.size} leads seleccionados</span>
          <div className="flex gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="text-sm text-slate-600 hover:text-slate-800">Deseleccionar</button>
            <button data-testid="bulk-delete-btn" onClick={handleBulkDelete} className="bg-red-600 text-white px-4 py-1.5 rounded text-sm font-medium hover:bg-red-700">Eliminar Seleccionados</button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="activity-timeline">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : activities.length > 0 ? (
          <div className="divide-y divide-slate-100 px-5">
            {activities.filter(a => a.type === 'lead').length > 0 && (
              <div className="py-2 flex items-center gap-2">
                <input type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === activities.filter(a => a.type === 'lead').length}
                  onChange={toggleSelectAll} className="w-4 h-4 accent-blue-600" />
                <span className="text-xs text-slate-500">Seleccionar todos los leads</span>
              </div>
            )}
            {activities.map((item) => (
              <ActivityItem key={item.id + item.timestamp} item={item}
                selected={selectedIds.has(item.related_id || item.id)}
                onToggle={toggleSelect} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <Lightning size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-2">Sin actividad reciente</p>
            <p className="text-sm text-slate-400">La actividad aparecera aqui automaticamente</p>
          </div>
        )}
      </div>
    </div>
  );
}
